/**
 * Amplify v3 to v6 Compatibility Layer
 * Provides backward-compatible API for gradual migration
 */

import {
  fetchAuthSession,
  getCurrentUser,
  signOut as amplifySignOut,
  signInWithRedirect,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import store from '@/store';
import { getApiKey } from '@/utils/apiKeys';

// Store for dynamic API configurations
const dynamicApiConfigs = new Map();

// Helper to register dynamic API endpoint
export function registerApiEndpoint(name, config) {
  dynamicApiConfigs.set(name, config);
}

// Helper to get API config
function getApiConfig(apiName) {
  return dynamicApiConfigs.get(apiName);
}

// Auth compatibility layer
export const Auth = {
  async currentSession() {
    const session = await fetchAuthSession();
    // Return v3-like structure
    return {
      getIdToken: () => ({
        getJwtToken: () => session.tokens?.idToken?.toString() || '',
      }),
      getAccessToken: () => ({
        getJwtToken: () => session.tokens?.accessToken?.toString() || '',
      }),
    };
  },

  async currentAuthenticatedUser() {
    return await getCurrentUser();
  },

  async signOut(options = {}) {
    return await amplifySignOut({ global: false, ...options });
  },

  async federatedSignIn(options) {
    return await signInWithRedirect(options);
  },
};

// Helper to get auth headers with API key
async function getAuthHeaders(apiName, siteIdOverride) {
  const headers = {};

  try {
    const session = await fetchAuthSession();

    if (session?.tokens?.idToken) {
      headers.Authorization = `Bearer ${session.tokens.idToken.toString()}`;
    } else {
      console.warn('⚠️ No idToken in session');
    }
  } catch (error) {
    console.error('❌ Error fetching auth session:', error);
  }

  // Add x-api-key based on API name
  // Resolve the site ID from the apiName:
  //   'ASU00002'        -> ASU00002
  //   'proxy-ASU00002'  -> ASU00002
  //   'asulink' / 'proxy' -> siteIdOverride ?? selected_site_id
  const siteIdMatch = apiName.match(/ASU\d+/);
  const siteId = siteIdMatch ? siteIdMatch[0] : null;

  if (siteId || apiName === 'asulink' || apiName === 'proxy') {
    const resolvedSiteId = siteId || siteIdOverride || store.state.selected_site_id;
    const apiKey = getApiKey(resolvedSiteId);
    if (apiKey) {
      headers['x-api-key'] = apiKey;
    } else {
      console.error('❌ No API key found for site:', resolvedSiteId);
    }
  }

  return headers;
}

// API compatibility layer
export const API = {
  async get(apiName, path, init = {}) {
    const { queryStringParameters, headers: customHeaders, siteIdOverride, ...options } = init;
    const authHeaders = await getAuthHeaders(apiName, siteIdOverride);

    // Build the API path based on apiName and environment
    let apiPath = path;
    const isDev = process.env.NODE_ENV === 'development';
    const currentSiteId = siteIdOverride || store.state.selected_site_id;

    if ((apiName === 'proxy' || apiName.startsWith('proxy-')) && !path.startsWith('/api/')) {
      apiPath = `/api${path.startsWith('/') ? '' : '/'}${path}`;
    } else if (apiName === 'asulink' && isDev) {
      // In development, route asulink calls through /asulink/{site_id}/... for dynamic proxy
      apiPath = `/asulink/${currentSiteId}${path.startsWith('/') ? '' : '/'}${path}`;
    }

    // Build URL - in development, use relative paths for proxy
    let url;
    if (isDev) {
      // Use relative path that will be proxied by vue.config.js
      url = new URL(apiPath, window.location.origin);
    } else {
      // In production, use actual API endpoints
      const dynamicConfig = getApiConfig(apiName);
      const endpoint = dynamicConfig?.endpoint || this._getAmplifyEndpoint(apiName, currentSiteId);
      url = new URL(apiPath, endpoint);
    }

    if (queryStringParameters) {
      Object.entries(queryStringParameters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((item) => url.searchParams.append(key, item));
        } else {
          url.searchParams.append(key, value);
        }
      });
    }

    // Merge headers
    const headers = {
      ...authHeaders,
      ...customHeaders,
    };

    // Use fetch for ALL requests to ensure headers are sent correctly
    let response;
    try {
      response = await fetch(url.toString(), {
        method: 'GET',
        headers,
        ...options,
      });
    } catch (fetchError) {
      throw new Error(`Network error: ${fetchError.message}`);
    }

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unable to read error response');
      throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
    }

    return await response.json();
  },

  _getAmplifyEndpoint(apiName, siteId) {
    // In production, use actual API endpoints
    const currentSiteId = siteId || store.state.selected_site_id;
    if (apiName === 'asulink') {
      return process.env.VUE_APP_ASULINK_API_URL?.replace('site_id', currentSiteId.toLowerCase());
    } else if (apiName === 'proxy') {
      return process.env.VUE_APP_PROXY_API_URL;
    }
    throw new Error(`Unknown API name: ${apiName}`);
  },

  async post(apiName, path, init = {}) {
    const { body: requestBody, headers: customHeaders, ...options } = init;
    const authHeaders = await getAuthHeaders(apiName);
    const isDev = process.env.NODE_ENV === 'development';
    const currentSiteId = store.state.selected_site_id;

    // Build the API path based on apiName and environment
    let apiPath = path;
    if ((apiName === 'proxy' || apiName.startsWith('proxy-')) && !path.startsWith('/api/')) {
      apiPath = `/api${path.startsWith('/') ? '' : '/'}${path}`;
    } else if (apiName === 'asulink' && isDev) {
      apiPath = `/asulink/${currentSiteId}${path.startsWith('/') ? '' : '/'}${path}`;
    }

    let url;
    if (isDev) {
      url = new URL(apiPath, window.location.origin);
    } else {
      const endpoint = getApiConfig(apiName)?.endpoint || this._getAmplifyEndpoint(apiName);
      url = new URL(apiPath, endpoint);
    }

    const headers = {
      ...authHeaders,
      ...customHeaders,
      'Content-Type': 'application/json',
    };

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
      ...options,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unable to read error response');
      throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
    }

    return await response.json();
  },

  async put(apiName, path, init = {}) {
    const { body: requestBody, headers: customHeaders, ...options } = init;
    const authHeaders = await getAuthHeaders(apiName);
    const isDev = process.env.NODE_ENV === 'development';
    const currentSiteId = store.state.selected_site_id;

    // Build the API path based on apiName and environment
    let apiPath = path;
    if ((apiName === 'proxy' || apiName.startsWith('proxy-')) && !path.startsWith('/api/')) {
      apiPath = `/api${path.startsWith('/') ? '' : '/'}${path}`;
    } else if (apiName === 'asulink' && isDev) {
      apiPath = `/asulink/${currentSiteId}${path.startsWith('/') ? '' : '/'}${path}`;
    }

    let url;
    if (isDev) {
      url = new URL(apiPath, window.location.origin);
    } else {
      const endpoint = getApiConfig(apiName)?.endpoint || this._getAmplifyEndpoint(apiName);
      url = new URL(apiPath, endpoint);
    }

    const headers = {
      ...authHeaders,
      ...customHeaders,
      'Content-Type': 'application/json',
    };

    const response = await fetch(url.toString(), {
      method: 'PUT',
      headers,
      body: JSON.stringify(requestBody),
      ...options,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unable to read error response');
      throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
    }

    return await response.json();
  },

  async del(apiName, path, init = {}) {
    const { headers: customHeaders, ...options } = init;
    const authHeaders = await getAuthHeaders(apiName);
    const isDev = process.env.NODE_ENV === 'development';
    const currentSiteId = store.state.selected_site_id;

    // Build the API path based on apiName and environment
    let apiPath = path;
    if ((apiName === 'proxy' || apiName.startsWith('proxy-')) && !path.startsWith('/api/')) {
      apiPath = `/api${path.startsWith('/') ? '' : '/'}${path}`;
    } else if (apiName === 'asulink' && isDev) {
      apiPath = `/asulink/${currentSiteId}${path.startsWith('/') ? '' : '/'}${path}`;
    }

    let url;
    if (isDev) {
      url = new URL(apiPath, window.location.origin);
    } else {
      const endpoint = getApiConfig(apiName)?.endpoint || this._getAmplifyEndpoint(apiName);
      url = new URL(apiPath, endpoint);
    }

    const headers = {
      ...authHeaders,
      ...customHeaders,
    };

    const response = await fetch(url.toString(), {
      method: 'DELETE',
      headers,
      ...options,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unable to read error response');
      throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
    }

    // A successful DELETE conventionally replies 204 No Content. Calling
    // response.json() on an empty body throws SyntaxError, which callers would
    // report to the user as a failed delete.
    if (response.status === 204) return null;
    const text = await response.text();
    if (!text) return null;
    return JSON.parse(text);
  },

  // Alias for del - some code uses API.delete
  async delete(apiName, path, init = {}) {
    return this.del(apiName, path, init);
  },

  async patch(apiName, path, init = {}) {
    const { body: requestBody, headers: customHeaders, ...options } = init;
    const authHeaders = await getAuthHeaders(apiName);
    const isDev = process.env.NODE_ENV === 'development';
    const currentSiteId = store.state.selected_site_id;

    // Build the API path based on apiName and environment
    let apiPath = path;
    if ((apiName === 'proxy' || apiName.startsWith('proxy-')) && !path.startsWith('/api/')) {
      apiPath = `/api${path.startsWith('/') ? '' : '/'}${path}`;
    } else if (apiName === 'asulink' && isDev) {
      apiPath = `/asulink/${currentSiteId}${path.startsWith('/') ? '' : '/'}${path}`;
    }

    let url;
    if (isDev) {
      url = new URL(apiPath, window.location.origin);
    } else {
      const endpoint = getApiConfig(apiName)?.endpoint || this._getAmplifyEndpoint(apiName);
      url = new URL(apiPath, endpoint);
    }

    const headers = {
      ...authHeaders,
      ...customHeaders,
      'Content-Type': 'application/json',
    };

    const response = await fetch(url.toString(), {
      method: 'PATCH',
      headers,
      body: JSON.stringify(requestBody),
      ...options,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unable to read error response');
      throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
    }

    return await response.json();
  },
};

// AuthState enum
export const AuthState = {
  SignIn: 'signIn',
  SignUp: 'signUp',
  SignedIn: 'signedIn',
  SignedOut: 'signedOut',
  ConfirmSignIn: 'confirmSignIn',
  ConfirmSignUp: 'confirmSignUp',
  ForgotPassword: 'forgotPassword',
};

// Auth state change listener using Hub
export const onAuthUIStateChange = (callback) => {
  // Listen to auth events via Hub
  const hubListener = Hub.listen('auth', async ({ payload }) => {
    const { event, data } = payload;
    switch (event) {
      case 'signedIn':
        try {
          const session = await fetchAuthSession();

          if (session?.tokens?.idToken) {
            callback(AuthState.SignedIn, data);
          } else {
            console.log('❌ No valid tokens - ignoring signedIn event');
          }
        } catch (error) {
          console.error('❌ Error checking session:', error);
        }
        break;

      case 'signedOut':
        callback(AuthState.SignedOut, null);
        break;

      case 'tokenRefresh':
        // Token refresh is normal - Amplify has already updated the tokens internally
        // Verify the session is still valid after refresh
        try {
          const refreshedSession = await fetchAuthSession();
          if (!refreshedSession?.tokens?.idToken) {
            // Token refresh succeeded but no valid tokens - sign out
            callback(AuthState.SignedOut, null);
          }
          // Otherwise, tokens are valid and Amplify has already stored them
        } catch (error) {
          console.error('Error verifying refreshed session:', error);
          callback(AuthState.SignedOut, null);
        }
        break;

      case 'tokenRefresh_failure':
        // Token refresh failed - sign out the user
        callback(AuthState.SignedOut, null);
        break;

      default:
        break;
    }
  });

  // Check initial auth state with retry for OAuth redirects

  const checkAuthWithRetry = async (retriesLeft = 3) => {
    try {
      const user = await getCurrentUser();

      // Don't use forceRefresh to avoid triggering unnecessary token refresh events
      const session = await fetchAuthSession();

      if (session?.tokens?.idToken) {
        callback(AuthState.SignedIn, user);
      } else if (retriesLeft > 0) {
        setTimeout(() => checkAuthWithRetry(retriesLeft - 1), 500);
      } else {
        console.log('❌ No valid initial tokens after retries');
      }
    } catch (error) {
      if (retriesLeft > 0 && error.message?.includes('not authenticated')) {
        setTimeout(() => checkAuthWithRetry(retriesLeft - 1), 500);
      } else {
        console.log('ℹ️ No authenticated user on initial load:', error.message || error);
      }
    }
  };

  setTimeout(() => checkAuthWithRetry(), 0);

  // Return unsubscribe function
  return () => hubListener();
};
